/* eslint-disable react/jsx-props-no-spreading */
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { TextField } from '@mui/material';
import { UPInputLabel } from 'components/input/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { profileSelector } from 'store/selectors/profile-selectors';
import nlLocale from 'date-fns/locale/nl';
import frLocale from 'date-fns/locale/fr';
import enLocale from 'date-fns/locale/en-US';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { toNumber } from 'lodash';
import { getISO8601WeekNumber, getISO8601Year } from 'modules/contracts/helpers';
import { HEADER__WEEK_PICKER_MULTIPLE_HINT } from 'translations/constants';
import { UPWeekPickerProps, UPWeekPickerWeek } from './types';
import {
  CustomPickersDay,
  CustomWeekDisplay,
  UPDatepickerContainerStyled,
  UPDatepickerHelperText,
  WeekPickerHint,
} from './styles';

const MultipleWeeksHint = (): JSX.Element => {
  const [t] = useTranslation();
  return <WeekPickerHint>{t(HEADER__WEEK_PICKER_MULTIPLE_HINT)}</WeekPickerHint>;
};

const UPWeekPicker = (props: UPWeekPickerProps): JSX.Element => {
  const userLanguage = useAppSelector(profileSelector).languageId;
  const {
    value,
    className,
    onChange,
    label,
    required,
    error,
    helperText,
    weekValue,
    minDate,
    multiple,
    selectedWeeks,
    onWeeksChange,
  } = props;
  const handleCalendarLanguage = useMemo(() => {
    switch (userLanguage) {
      case '1':
        nlLocale.options.weekStartsOn = 1;
        return nlLocale;
      case '2':
        frLocale.options.weekStartsOn = 1;
        return frLocale;
      default:
        enLocale.options.weekStartsOn = 1;
        return enLocale;
    }
  }, [userLanguage]);

  const getSpecialDayInputFormat = (dateString: Date): string => {
    const date = new Date(dateString);

    return `${getISO8601WeekNumber(date)} / ${getISO8601Year(date)}`;
  };

  const weeks = useMemo(() => selectedWeeks ?? [], [selectedWeeks]);

  const isWeekSelected = (date: Date): boolean => {
    const weekNo = toNumber(getISO8601WeekNumber(date));
    if (!multiple) return weekNo === weekValue;
    const weekYear = toNumber(getISO8601Year(date));
    return weeks.some(w => w.week === weekNo && w.year === weekYear);
  };

  const onToggleWeek = (date: Date) => {
    const toggled: UPWeekPickerWeek = {
      week: toNumber(getISO8601WeekNumber(date)),
      year: toNumber(getISO8601Year(date)),
    };
    const isSelected = weeks.some(w => w.week === toggled.week && w.year === toggled.year);
    if (isSelected && weeks.length === 1) return;
    const next = isSelected
      ? weeks.filter(w => w.week !== toggled.week || w.year !== toggled.year)
      : [...weeks, toggled].sort((a, b) => a.year - b.year || a.week - b.week);
    onWeeksChange?.(next);
  };

  const getMultipleWeeksInputFormat = (): string => {
    if (weeks.length === 0) return getSpecialDayInputFormat(value);
    const [first] = weeks;
    const rest = weeks.length > 1 ? ` +${weeks.length - 1}` : '';
    return `${first.week} / ${first.year}${rest}`;
  };

  return (
    <UPDatepickerContainerStyled className={className} error={error}>
      <UPInputLabel>
        {label} {required && '*'}
      </UPInputLabel>
      <LocalizationProvider locale={handleCalendarLanguage} dateAdapter={AdapterDateFns}>
        <DatePicker
          value={value}
          onChange={onChange}
          renderInput={params => (
            <TextField
              {...params}
              onKeyDown={e => {
                e.preventDefault();
              }}
            />
          )}
          renderDay={(date: any, selectedDates, pickersDayProps) => {
            const isSunday = date.getDay() === 1;
            const weekNo = getISO8601WeekNumber(date);
            const isSelectedWeek = isWeekSelected(date);
            return (
              <div key={date.toDateString()}>
                {isSunday && (
                  <CustomWeekDisplay
                    isSelected={isSelectedWeek}
                    clickable={multiple}
                    onClick={multiple ? () => onToggleWeek(date) : undefined}
                  >
                    W {weekNo}
                  </CustomWeekDisplay>
                )}
                {multiple ? (
                  <CustomPickersDay
                    {...pickersDayProps}
                    selected={false}
                    inSelectedWeek={isSelectedWeek}
                    onDaySelect={day => onToggleWeek(day as Date)}
                    disableMargin
                  />
                ) : (
                  <CustomPickersDay {...pickersDayProps} disableMargin />
                )}
              </div>
            );
          }}
          inputFormat={multiple ? getMultipleWeeksInputFormat() : getSpecialDayInputFormat(value)}
          showDaysOutsideCurrentMonth
          disableHighlightToday
          components={{
            OpenPickerIcon: ArrowDropDownIcon,
            ...(multiple && { ActionBar: MultipleWeeksHint }),
          }}
          mask="__ / ____"
          disableMaskedInput={multiple}
          views={['day']}
          PaperProps={{
            className: 'week-picker',
            ...(multiple && {
              sx: {
                '.MuiDayPicker-slideTransition': { minHeight: 'auto' },
                '.MuiDayPicker-slideTransition > :not(.PrivatePickersSlideTransition-slideExit)': {
                  position: 'relative',
                },
              },
            }),
          }}
          minDate={minDate || undefined}
        />
      </LocalizationProvider>
      {error && <UPDatepickerHelperText>{helperText}</UPDatepickerHelperText>}
    </UPDatepickerContainerStyled>
  );
};

export default UPWeekPicker;
